import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const MODE_STORAGE_KEY = 'portfolio-color-mode';
const PALETTE_STORAGE_KEY = 'portfolio-color-palette';
const CUSTOM_COLORS_STORAGE_KEY = 'portfolio-custom-colors';
const DESIGN_STORAGE_KEY = 'portfolio-design';

export const DESIGN_SYSTEMS = [
  {
    id: 'aurora-glass',
    name: 'Aurora Glass',
    description: 'Futuristic glass interface with violet and cyan energy.',
    source: 'src1',
    colors: { primary: '#6c5ce7', secondary: '#4f46c7', accent: '#22d3ee' },
  },
  {
    id: 'genz-hyperpop',
    name: 'Gen Z Hyperpop',
    description: 'Playful sticker-inspired layout with bold pink and acid lime.',
    source: 'src2',
    colors: { primary: '#ff3cac', secondary: '#d91b88', accent: '#c8ff35' },
  },
  {
    id: 'executive-blue',
    name: 'Executive Blue',
    description: 'Clean corporate portfolio with structured blue accents.',
    source: 'src3',
    colors: { primary: '#2563eb', secondary: '#1d4ed8', accent: '#60a5fa' },
  },
  {
    id: 'premium-dark',
    name: 'Premium Dark',
    description: 'Midnight studio aesthetic with champagne details.',
    source: 'src4',
    colors: { primary: '#a77b35', secondary: '#805b24', accent: '#d8ba7a' },
  },
  {
    id: 'boutique-green',
    name: 'Boutique Green',
    description: 'Editorial consultancy style in ivory and forest green.',
    source: 'src5',
    colors: { primary: '#087f5b', secondary: '#056246', accent: '#5cc99e' },
  },
  {
    id: 'bento-grid',
    name: 'Professional Bento',
    description: 'Modular dashboard layout with balanced bento cards.',
    source: 'src6',
    colors: { primary: '#0d9488', secondary: '#0f766e', accent: '#5eead4' },
  },
  {
    id: 'enterprise-blue',
    name: 'Enterprise Blue',
    description: 'Restrained enterprise interface with crisp hierarchy.',
    source: 'src7',
    colors: { primary: '#155eef', secondary: '#004eeb', accent: '#84adff' },
  },
  {
    id: 'editorial-burgundy',
    name: 'Editorial Burgundy',
    description: 'Executive editorial design with a refined burgundy accent.',
    source: 'src8',
    colors: { primary: '#8c2433', secondary: '#681824', accent: '#d28b95' },
  },
];

export const COLOR_PALETTES = [
  { id: 'design-default', name: 'Design Default', colors: null },
  {
    id: 'ocean',
    name: 'Ocean',
    colors: { primary: '#2563eb', secondary: '#1d4ed8', accent: '#60a5fa' },
  },
  {
    id: 'violet',
    name: 'Violet',
    colors: { primary: '#7c3aed', secondary: '#5b21b6', accent: '#a78bfa' },
  },
  {
    id: 'emerald',
    name: 'Emerald',
    colors: { primary: '#059669', secondary: '#047857', accent: '#6ee7b7' },
  },
  {
    id: 'rose',
    name: 'Rose',
    colors: { primary: '#e11d48', secondary: '#be123c', accent: '#fda4af' },
  },
  {
    id: 'orange',
    name: 'Orange',
    colors: { primary: '#ea580c', secondary: '#c2410c', accent: '#fdba74' },
  },
  {
    id: 'cyan',
    name: 'Cyan',
    colors: { primary: '#0891b2', secondary: '#0e7490', accent: '#67e8f9' },
  },
  {
    id: 'champagne',
    name: 'Champagne',
    colors: { primary: '#a77b35', secondary: '#805b24', accent: '#d8ba7a' },
  },
  {
    id: 'burgundy',
    name: 'Burgundy',
    colors: { primary: '#8c2433', secondary: '#681824', accent: '#d28b95' },
  },
  {
    id: 'graphite',
    name: 'Graphite',
    colors: { primary: '#475569', secondary: '#334155', accent: '#94a3b8' },
  },
  { id: 'custom', name: 'Custom', colors: null },
];

const DEFAULT_CUSTOM_COLORS = { primary: '#2563eb', secondary: '#1d4ed8', accent: '#60a5fa' };
const ThemeContext = createContext(undefined);

const readStorage = (key, fallback) => {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch (error) {
    return fallback;
  }
};

const readStoredColors = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(CUSTOM_COLORS_STORAGE_KEY));
    return parsed?.primary && parsed?.secondary && parsed?.accent ? parsed : DEFAULT_CUSTOM_COLORS;
  } catch (error) {
    return DEFAULT_CUSTOM_COLORS;
  }
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within a ThemeProvider');
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [designId, setDesignId] = useState(() => {
    try {
      const saved = localStorage.getItem(DESIGN_STORAGE_KEY);
      if (saved && DESIGN_SYSTEMS.some((d) => d.id === saved)) return saved;
    } catch (_) { /* storage unavailable */ }
    return DESIGN_SYSTEMS[Math.floor(Math.random() * DESIGN_SYSTEMS.length)].id;
  });
  const [darkMode, setDarkMode] = useState(() => readStorage(MODE_STORAGE_KEY, 'light') === 'dark');
  const [paletteId, setPaletteId] = useState(() => {
    const saved = readStorage(PALETTE_STORAGE_KEY, 'design-default');
    return COLOR_PALETTES.some((palette) => palette.id === saved) ? saved : 'design-default';
  });
  const [customColors, setCustomColors] = useState(readStoredColors);

  const currentDesign = useMemo(
    () => DESIGN_SYSTEMS.find((design) => design.id === designId) || DESIGN_SYSTEMS[0],
    [designId]
  );
  const selectedPalette = useMemo(
    () => COLOR_PALETTES.find((palette) => palette.id === paletteId) || COLOR_PALETTES[0],
    [paletteId]
  );
  const activeColors = useMemo(() => {
    if (paletteId === 'custom') return customColors;
    return selectedPalette.colors || currentDesign.colors;
  }, [paletteId, selectedPalette, customColors, currentDesign]);

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.design = currentDesign.id;
    root.dataset.palette = paletteId;
    root.classList.toggle('dark-mode', darkMode);
    root.style.setProperty('--accent', activeColors.primary);
    root.style.setProperty('--accent-dark', activeColors.secondary);
    root.style.setProperty('--accent-light', activeColors.accent);

    try {
      localStorage.setItem(MODE_STORAGE_KEY, darkMode ? 'dark' : 'light');
      localStorage.setItem(PALETTE_STORAGE_KEY, paletteId);
      localStorage.setItem(CUSTOM_COLORS_STORAGE_KEY, JSON.stringify(customColors));
      localStorage.setItem(DESIGN_STORAGE_KEY, currentDesign.id);
    } catch (error) {
      console.warn('Failed to save interface preferences:', error);
    }
  }, [currentDesign, darkMode, paletteId, customColors, activeColors]);

  const selectDesign = useCallback((id) => {
    if (DESIGN_SYSTEMS.some((design) => design.id === id)) setDesignId(id);
  }, []);
  const cycleDesign = useCallback(() => {
    const index = DESIGN_SYSTEMS.findIndex((design) => design.id === currentDesign.id);
    selectDesign(DESIGN_SYSTEMS[(index + 1) % DESIGN_SYSTEMS.length].id);
  }, [currentDesign.id, selectDesign]);
  const toggleTheme = useCallback(() => setDarkMode((value) => !value), []);
  const setThemeMode = useCallback((mode) => setDarkMode(mode === 'dark'), []);
  const selectPalette = useCallback((id) => {
    if (COLOR_PALETTES.some((palette) => palette.id === id)) setPaletteId(id);
  }, []);
  const updateCustomColor = useCallback((key, value) => {
    if (!['primary', 'secondary', 'accent'].includes(key)) return;
    setCustomColors((colors) => ({ ...colors, [key]: value }));
    setPaletteId('custom');
  }, []);

  // All deps are stable: darkMode/currentDesign/paletteId/customColors are state values,
  // and all callbacks are wrapped in useCallback with stable deps.
  const value = useMemo(
    () => ({
      isDarkMode: darkMode,
      themeMode: darkMode ? 'dark' : 'light',
      currentDesign,
      designs: DESIGN_SYSTEMS,
      selectDesign,
      cycleDesign,
      toggleTheme,
      setThemeMode,
      palettes: COLOR_PALETTES,
      paletteId,
      selectedPalette,
      activeColors,
      customColors,
      selectPalette,
      updateCustomColor,
    }),
    [
      darkMode,
      currentDesign,
      selectDesign,
      cycleDesign,
      toggleTheme,
      setThemeMode,
      paletteId,
      selectedPalette,
      activeColors,
      customColors,
      selectPalette,
      updateCustomColor,
    ]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
